import { useMemo } from 'react'

import { useJourneysStore } from '@/features/journeys/stores/useJourneysStore'
import type { Level } from '@/features/journeys'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useOptimizedRecord } from '@/shared/hooks/useOptimizedQuery'

import {
  findFallbackNextLevelUid,
  resolveCompletionNextAction,
  type CompletionNextAction
} from '../domain/resolveCompletionNextAction'

/**
 * Where the student goes after a level they cannot play again.
 *
 * Same resolution the finish summary uses (API `next` first, journey structure
 * as fallback), lifted out of StepSummary so the redo-blocked screen can offer
 * a way forward instead of a dead end.
 */
export function useNextLevelTarget(
  levelUid: string | null,
  journeyId: number | null
): CompletionNextAction {
  const { progress } = useUserProgress()
  const {
    journeys,
    isLoading: isJourneysLoading,
    isError: isJourneysError
  } = useJourneysStore()

  const {
    record: nextLevel,
    isLoading: isNextLevelLoading,
    isFetching: isNextLevelFetching,
    isError: isNextLevelError
  } = useOptimizedRecord<Level>({
    path: `/user/levels/${levelUid}/next`,
    enabled: Boolean(levelUid)
  })

  const currentJourney = useMemo(
    () => journeys.find(journey => journey.id === journeyId) ?? null,
    [journeys, journeyId]
  )

  const fallbackNextLevelUid = useMemo(
    () =>
      findFallbackNextLevelUid({
        currentLevelUid: levelUid,
        journey: currentJourney,
        finishedLevelIds: progress?.finished_levels ?? []
      }),
    [currentJourney, levelUid, progress?.finished_levels]
  )

  return useMemo(
    () =>
      resolveCompletionNextAction({
        currentLevelUid: levelUid,
        apiNextLevelUid: nextLevel?.uid ?? null,
        fallbackNextLevelUid,
        // Nada acabou de ser concluído aqui: o progresso já é o vigente.
        isProgressRefreshPending: false,
        didProgressRefreshFail: false,
        isNextLevelLookupPending:
          !nextLevel?.uid && (isNextLevelLoading || isNextLevelFetching),
        didNextLevelLookupFail: isNextLevelError,
        isJourneyDataPending: isJourneysLoading,
        didJourneyDataFail: isJourneysError,
        hasJourneyData: Boolean(currentJourney)
      }),
    [
      currentJourney,
      fallbackNextLevelUid,
      isJourneysError,
      isJourneysLoading,
      isNextLevelError,
      isNextLevelFetching,
      isNextLevelLoading,
      levelUid,
      nextLevel?.uid
    ]
  )
}
